/**
 * Handbuch — vollständige Bedienungsanleitung (eigener Menüpunkt).
 * Statischer Inhalt als Markdown, gerendert über die geteilte Markdown-Komponente.
 * Für alle Rollen sichtbar; rollenspezifische Abschnitte sind gekennzeichnet.
 */

import { Markdown } from "@/components/Markdown"

const HANDBUCH = `
# Handbuch

Willkommen im **Testerwerk** — dem Feedback-Portal für eure Ninox-Anwendung.
Hier meldet ihr Fehler und Änderungswünsche mit Screenshot und
KI-geführtem Dialog. Aus jeder Meldung entsteht eine strukturierte Anforderung,
die netz-fabrik sichtet und in ein Entwicklungs-Ticket überführt.

---

## 1. Überblick

- **Melden:** Fehler oder Änderungswunsch erfassen — mit Screenshot der Ninox-Oberfläche, Markierungen und einem kurzen KI-Dialog.
- **Übersicht:** Alle eigenen (bzw. für die Organisation sichtbaren) Meldungen verfolgen, filtern und durchsuchen.
- **Chat:** Zu jeder Meldung direkt mit der Entwicklung schreiben.
- Die Ninox-Anwendung selbst wird **nicht** verändert — der Testerwerk liest nur die Struktur (read-only) und hilft beim Beschreiben.

## 2. Anmeldung & Rollen

Die Anmeldung erfolgt über den **zentralen Login der Netzfabrik** — euer Zugang wird von netz-fabrik angelegt; ihr bekommt eine Einladungsmail mit einem Link, über den ihr einmalig euer Passwort setzt (7 Tage gültig). Das ist ein **eigener Login, unabhängig von eurer Anwendung**: in der Anwendung meldet ihr euch wie gewohnt an, im Testerwerk mit der E-Mail-Adresse aus der Einladung.

Gehört ihr zu **mehreren Organisationen**, erscheint oben in der Kopfzeile ein Umschalter — alle Listen zeigen dann nur die gewählte Organisation („Alle Organisationen" hebt die Einengung auf). Zugänge, Rollen und Sperrungen verwaltet netz-fabrik im ERP; Änderungen gelten beim nächsten Login.

Welche Funktionen ihr seht, hängt von eurer Rolle ab:

| Rolle | Darf … |
|---|---|
| **Kundennutzer** | Meldungen erfassen und absenden, **alle Meldungen eurer Organisation** lesen und in der Unterhaltung mitschreiben (bei fremden Meldungen als „ebenfalls betroffen" gekennzeichnet) |
| **Kundenadmin** | Zusätzlich: Priorität ändern, Meldungen verwerfen, unter „Organisation" einen **eigenen KI-Schlüssel** hinterlegen (sonst gilt der Schlüssel von netz-fabrik) |
| **Reviewer** (netz-fabrik) | Zusätzlich: Meldungen sichten, mit Ninox-Logik präzisieren, freigeben, Administration & Schema |
| **Eingeschränkter Reviewer** | Wie Reviewer, aber **ohne** Schema-Änderungshistorie und in der Administration **nur** den Verbrauch |

## 3. Eine Meldung erfassen (Menüpunkt „Melden")

1. **Ninox öffnen.** Über die Schaltfläche *In Ninox öffnen* startet ihr eure Ninox-Anwendung in einem **separaten Browser-Tab** (je nach Konfiguration *Live* oder *DEV*). Navigiert dort zu der Stelle, die ihr melden möchtet.
2. **Ninox-Tab verbinden.** Zurück im Testerwerk klickt ihr auf *Ninox-Tab verbinden* und gebt im Browser-Dialog den Ninox-Tab frei. Der verbundene Tab dient als Live-Vorschau.
3. **Screenshot aufnehmen.** Ein Klick auf die Vorschau erstellt einen Screenshot des aktuellen Ninox-Bildes.
4. **Markieren & schwärzen.** Markiert den relevanten Bereich mit Rechtecken/Pfeilen. Sensible Inhalte könnt ihr **schwärzen** — Schwärzungen werden **vor dem Hochladen** fest ins Bild eingebrannt (siehe Datenschutz).
5. **KI-Dialog führen.** Beschreibt Fehler oder Wunsch in eigenen Worten. Die KI stellt Rückfragen, erkennt anhand von Screenshots und Beschreibung selbst, **welche Tabelle und Stelle** der Datenbank gemeint ist, und erstellt daraus ein **Mini-PRD** mit *Ist-Zustand*, *Soll-Zustand* und *Akzeptanzkriterien*.
6. **Prüfen & absenden.** Kontrolliert das Ergebnis und sendet die Meldung ab. Bis dahin ist sie ein **Entwurf** und nur für euch sichtbar; ihr könnt sie jederzeit verwerfen.

> **Web-Anwendungen** (kein Ninox) funktionieren genauso: *Anwendung öffnen* (bzw. *Stage*/*Live*) startet sie in einem eigenen Browser-Tab, *Anwendungs-Tab verbinden* gibt ihn frei, dann Screenshot per Klick auf die Vorschau. Die Anwendung wird **nicht mehr im Testerwerk eingebettet** — ihr arbeitet dort wie gewohnt, der Tester greift nur das Bild ab. Eure Anmeldung gilt per SSO in beiden Tabs.

> **Tipp:** Ihr könnt mehrere Screenshots hinzufügen und die Meldung später weiterbearbeiten, solange sie ein Entwurf ist.

## 4. Übersicht — Meldungen verfolgen

Die **Übersicht** ist die Startseite und zeigt die Meldungen eurer Organisation als Karten — eure eigenen tragen links einen Farbbalken.

### Pill-Zeile: was auf euch wartet
Ganz oben — **unabhängig von allen Filtern** — erscheinen anklickbare Pillen:
- **Blau „Von der Entwicklung":** Meldungen mit neuen Nachrichten oder offenen Fragen der Entwicklung an euch. Fragen tragen ein gelbes **Frage**-Schild und bleiben stehen, bis ihr geantwortet habt; alles andere verschwindet, sobald ihr die Unterhaltung geöffnet habt.
- Der Ungelesen-Stand gilt **geräteübergreifend** — was ihr am Rechner gelesen habt, ist auch am Handy gelesen.

### Status-Filter
- Standardmäßig werden **nur offene** Meldungen angezeigt (Neu, Gesichtet, In Umsetzung).
- **Klick** auf einen Status-Chip nimmt diesen Status hinzu oder entfernt ihn.
- **Doppelklick** auf einen Chip zeigt **nur** diesen Status (alle anderen werden abgewählt).
- *Nur offene* setzt auf die Standardauswahl zurück.

### Datumsstrahl
- Über die Schaltfläche **Datumsstrahl** blendet ihr einen horizontal scrollbaren Tages-Streifen ein. Er springt beim Einschalten auf **heute**.
- Ein Klick auf einen Tag zeigt die Meldungen, die **an diesem Tag erstellt** wurden.
- Bei aktivem Datumsstrahl ist der Status-Filter deaktiviert (es werden alle Stati des Tages gezeigt).

### Schnellsuche
- Öffnet die Suche mit **⌘K** bzw. **Strg + K** (oder über *Suchen* in der Kopfleiste).
- Bei **mehreren Wörtern** müssen **alle** vorkommen (Und-Suche) — verteilt über die Felder und in beliebiger Reihenfolge.
- Durchsucht werden **alle** in der Übersicht sichtbaren Angaben: Titel, Beschreibungen, Akzeptanzkriterien, Melder-Name, Ninox-Stelle und die #Nummer (Meldung oder Issue).

### Entwurf löschen
- Auf einer **Entwurfs-Karte** könnt ihr direkt auf das **Papierkorb-Symbol** klicken und den Entwurf nach Rückfrage endgültig löschen.

## 5. Meldungs-Detail & Unterhaltung

Ein Klick auf eine Meldung öffnet die **Detailansicht** mit Ist/Soll, Akzeptanzkriterien, Screenshots und der betroffenen Stelle. **Screenshots lassen sich per Klick in voller Größe öffnen** (blättern mit den Pfeiltasten, Esc schließt).

Das Herzstück ist die **Unterhaltung** — ein durchgehender Gesprächsstrang je Meldung:

- **Gelbe Sprechblasen** sind Nachrichten von euch (Meldern), **blaue** von der Entwicklung — die kleine Legende über dem Strang erklärt es an Ort und Stelle.
- Zwischen den Nachrichten stehen **Ereigniszeilen**: „Meldung abgesendet", „An die Entwicklung übergeben", „Eine Lösung ist in Arbeit", „ausgeliefert" — ihr seht also jederzeit, was mit eurer Meldung passiert, ohne nachfragen zu müssen.
- Der Erfassungs-Dialog mit der KI liegt eingeklappt am Anfang des Strangs.
- **„Rückfrage offen — bitte antworten"** erscheint nur, wenn die Entwicklung euch wirklich eine **Frage** gestellt hat; mit eurer Antwort erlischt die Markierung. Beim Antworten hilft auf Wunsch die KI: **„Antwort mit KI-Hilfe formulieren"** macht aus Stichworten eine vollständige Antwort — gesendet wird erst, wenn ihr es freigebt.

### Erledigt heißt: ihr prüft
Meldet die Entwicklung eine Meldung als **erledigt**, bekommt ihr eine Nachricht „bitte prüfen Sie das Ergebnis" (blaue Pill in der Übersicht). Passt alles — nichts weiter zu tun. Passt etwas **nicht**, antwortet einfach in der Unterhaltung: die Meldung geht **automatisch zurück in die Umsetzung**, eine neue Meldung ist nicht nötig. Das gilt auch später — eine Ergänzung zu einer erledigten Meldung öffnet sie wieder.

## 6. Der Weg einer Meldung (Status)

| Status | Bedeutung |
|---|---|
| **Entwurf** | Noch nicht abgesendet, nur für euch sichtbar |
| **Neu** | Abgesendet, wartet auf Sichtung |
| **Gesichtet** | Von netz-fabrik geprüft/geschärft |
| **In Umsetzung** | An die Entwicklung übergeben und in Arbeit |
| **Erledigt** | Umgesetzt — bitte prüfen; eine Antwort in der Unterhaltung öffnet die Meldung wieder |
| **Abgelehnt / Verworfen** | Nicht weiterverfolgt (mit Begründung) |

## 7. Für Kundenadmins

- Die Meldungen der Organisation sehen inzwischen **alle** Nutzer (Filter *Meine/Andere* in der Übersicht); Kundenadmins können zusätzlich:
- **Priorität** einer Meldung anpassen.
- Noch nicht bearbeitete Meldungen **verwerfen** (mit Begründung).

## 8. Für Reviewer (netz-fabrik)

Zusätzlich zu den obigen Funktionen:

- **Review** (Menüpunkt *Review*): Meldungen **sichten** und schärfen.
- **Mit Ninox-Logik präzisieren:** Ein Assistent lädt die Formeln und Trigger der betroffenen Tabelle, löst abhängige Formeln bis zur Quelle auf und kennt die Ninox-Formelsprache. Der Vorschlag lässt sich per *Übernehmen* in die Meldung schreiben.
- **Freigeben:** Erzeugt aus der Meldung ein **Forgejo-Issue** (Entwicklungs-Ticket).
- **Administration** (*Administration*): KI-Anbieter und -Dienste konfigurieren, **Verbrauch** einsehen, Service-API-Token verwalten.
- **Schema** (*Schema*): Struktur-Browser der Ninox-Datenbanken, Änderungs-Historie und Schema-Backup-Läufe.

> **Eingeschränkter Reviewer:** sieht im Schema **keine** Änderungs-Historie und in der Administration **nur** den Verbrauch — alle übrigen Reviewer-Funktionen bleiben.

## 9. Datenschutz & Sicherheit

- **Screenshots:** Schwärzungen werden bereits **vor dem Upload** fest ins Bild eingebrannt — das gespeicherte Bild enthält keine geschwärzten Inhalte mehr.
- **KI sieht nur Struktur:** Bei der Erfassung nutzt die KI ausschließlich die **Struktur** eurer Datenbank (Tabellen/Felder), **niemals Daten**. Die volle Logik (Formeln/Trigger) ist netz-fabrik-intern und nur für Reviewer sichtbar.
- **Read-only:** Der Zugriff auf Ninox erfolgt strikt lesend — es werden keine Datensätze verändert.

## 10. Tipps & Fehlerbehebung

- **Tab-Freigabe:** Beim *Ninox-Tab verbinden* im Browser-Dialog genau den Ninox-Tab auswählen und freigeben. Wird die Freigabe beendet, einfach neu verbinden.
- **Kein Screenshot möglich?** Die Tab-Freigabe funktioniert in aktuellen Chromium-basierten Browsern (Chrome/Edge) am zuverlässigsten.
- **Keine KI-Antwort (Fehler 503)?** Dann ist noch kein KI-Anbieter hinterlegt — das richtet netz-fabrik in der Administration ein.
- **Meldung nicht auffindbar?** Prüft den **Status-Filter** (Standard: nur offene) bzw. schaltet den **Datumsstrahl** aus.

---

*Fragen, die dieses Handbuch nicht beantwortet? Schreibt sie direkt in die **Unterhaltung** einer Meldung — netz-fabrik hilft weiter.*
`

export function HandbuchPage() {
  return (
    <div className="mx-auto max-w-[820px]">
      <h1 className="mb-6 text-[26px] font-bold tracking-tight">Handbuch</h1>
      <div className="rounded-xl border bg-card p-6 shadow-card md:p-8">
        <Markdown inhalt={HANDBUCH} />
      </div>
    </div>
  )
}
